import { useEffect, useMemo, useState } from "react";

import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { ChevronsUpDown } from "lucide-react";

import { useToast } from "@/shared/hooks/use-toast";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import {
  useQpmCatalogIncludeMonitorStrictMutation,
  useQpmCatalogItemsListQuery,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import { useQpmMonitorListQuery } from "@/services/backend/qpm/monitors/api/monitor-api";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

export type MonitorSelectorComboBoxProps = {
  catalogId: string;
  monitorTypeKey: MonitorType;
  onCreateButtonVisibilityChange?: (visible: boolean) => void;
};

export function MonitorSelectorComboBox({
  catalogId,
  monitorTypeKey,
  onCreateButtonVisibilityChange,
}: MonitorSelectorComboBoxProps) {
  const { toast } = useToast();
  const { companyId } = useActiveCompany();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const {
    data: itemsRes,
    isLoading: isCatalogItemsLoading,
    error: catalogItemsError,
  } = useQpmCatalogItemsListQuery({
    catalogId,
    includedOnly: true,
    limit: 0,
    offset: 0,
  });

  const {
    data: monitorList,
    isLoading: isBaseListLoading,
    error: baseListError,
  } = useQpmMonitorListQuery({
    company: { active: true, values: [companyId] },
    catalog: { active: false, values: [] },
    type: { active: true, values: [monitorTypeKey] },
  });

  const [includeMonitor, includeState] =
    useQpmCatalogIncludeMonitorStrictMutation();

  useEffect(() => {
    if (!includeState.error) return;
    toast({ ...parseRTKQueryError(includeState.error) });
    includeState.reset();
  }, [includeState, toast]);

  const alreadyIncludedBaseIds = useMemo(() => {
    const all = itemsRes?.items ?? [];
    return new Set(
      all
        .filter((it) => it.included)
        .map((it) => it.baseMonitorId)
        .filter(Boolean),
    );
  }, [itemsRes?.items]);

  const availableBaseMonitors = useMemo(() => {
    const all = monitorList?.items ?? [];
    return all.filter((m) => !alreadyIncludedBaseIds.has(m.id));
  }, [alreadyIncludedBaseIds, monitorList?.items]);

  const filteredBaseMonitors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return availableBaseMonitors;

    return availableBaseMonitors.filter((m) => {
      const name = (m.name ?? "").toLowerCase();
      const supplier = (m.supplier ?? "").toLowerCase();
      return name.includes(q) || supplier.includes(q);
    });
  }, [availableBaseMonitors, search]);

  const hasNoMatch = filteredBaseMonitors.length === 0;
  const hasSearchValue = search.trim().length > 0;
  const showCreateButton =
    availableBaseMonitors.length === 0 || (hasSearchValue && hasNoMatch);

  useEffect(() => {
    onCreateButtonVisibilityChange?.(showCreateButton);
  }, [onCreateButtonVisibilityChange, showCreateButton]);

  const isLoading = isCatalogItemsLoading || isBaseListLoading;

  if (catalogItemsError) {
    return <RTKQueryErrorAlert error={catalogItemsError} />;
  }

  if (baseListError) {
    return <RTKQueryErrorAlert error={baseListError} />;
  }

  const handleSelect = (baseMonitorId: string) => {
    includeMonitor({ catalogId, monitorId: baseMonitorId, strict: true })
      .unwrap()
      .then(() => {
        setSearch("");
        setOpen(false);
      });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          disabled={isLoading || includeState.isLoading}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[320px] justify-between"
        >
          {t("Monitor wählen oder anlegen")}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" side="bottom" align="start">
        <Command>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={`${t("Suchen in")} ${MonitorTypeUtil.toLongString(monitorTypeKey)}...`}
          />
          <CommandList>
            <CommandEmpty>
              <div className="flex flex-col gap-3 px-6 py-4 text-sm">
                <span>{t("Kein Monitor gefunden")}</span>
              </div>
            </CommandEmpty>
            <CommandGroup heading={t("Verfügbare Basis Monitore")}>
              {filteredBaseMonitors.map((m) => (
                <CommandItem
                  key={m.id}
                  value={`${m.name ?? ""} ${m.supplier ?? ""}`}
                  onSelect={() => handleSelect(m.id)}
                >
                  <div className="flex w-full flex-col">
                    <span className="font-medium">{m.name}</span>
                    <span className="text-muted-foreground text-xs">
                      {m.supplier || t("Ohne Lieferant")}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
